import { Component, computed, effect, inject } from '@angular/core';
import { ActionsHeaderComponent, DialogService, NavigationService } from '@h5s/design-system';
import { IstatTypeEnum } from '@h5s/entities';
import { IActionButton } from '@h5s/util-button';
import { ActionType } from '@h5s/util-overlay';
import { finalize, Observable, of, switchMap } from 'rxjs';
import { FeatureIstatService } from '../../../feature-istat.service';
import { FeatureIstatStore } from '../../store/feature-istat.store';
import { FEATURE_ISTAT_TRANSLATIONS } from '../../translations/feature-istat.translations';

@Component({
  selector: 'h5s-istat-header',
  imports: [ActionsHeaderComponent],
  templateUrl: './istat-header.component.html',
  styleUrl: './istat-header.component.scss',
})
export class IstatHeaderComponent {
  private readonly featureIstatService = inject(FeatureIstatService);
  private readonly featureIstatStore = inject(FeatureIstatStore);
  private readonly dialogService = inject(DialogService);
  private readonly navigationService = inject(NavigationService);

  /* Translations */
  public readonly translation = FEATURE_ISTAT_TRANSLATIONS;

  /**
   * Variabili
   */
  public isFilterEmpty = this.featureIstatStore.isFilterEmpty;
  public hasSelectedTableIstatCustomer = this.featureIstatStore.hasSelectedTableIstatCustomer;
  public istatProfileIsChanged = this.featureIstatStore.istatProfileIsChanged;
  // Determina se l'istatType selezionato è stampabile
  public isSelectedIstatTypePrintable = this.featureIstatStore.isSelectedIstatTypePrintable;
  public isSelectedIstatTypeSendOnline = this.featureIstatStore.isSelectedIstatTypeSendOnline;

  public readonly actions = computed<IActionButton[]>(() => {
    const actions: IActionButton[] = [
      {
        description: this.translation.buttonActions,
        buttonStyle: 'primarySecondary',
        suffixIcon: 'uil uil-angle-down',

        subactions: [
          {
            description: this.translation.inHouseGuests,
            icon: 'uil-file-alt',
            action: (): void => {
              this.openInHouseGuestReportTab();
            },
          },
          {
            description: this.translation.generationParams,
            icon: 'uil-sliders-v-alt',
            action: (): void => {
              this.openGenerationParamsModal();
            },
            disabled: this.isFilterEmpty(),
          },
          {
            description: this.translation.selectReady,
            action: (): void => {
              this.featureIstatService.selectReadyIstatCustomer.next();
            },
            disabled: this.isFilterEmpty(),
          },
        ],
      },
    ];

    const sendOnlineIstatAction: IActionButton = {
      buttonStyle: 'primary',
      description: this.translation.buttonSendOnlineIstat,
      disabled: !this.hasSelectedTableIstatCustomer() || this.istatProfileIsChanged(),
      action: (): void => {
        this.sendIstatOnline();
      },
    };

    const generateIstatFileAction: IActionButton = {
      buttonStyle: 'primary',
      description: this.translation.buttonGenerateIstatFile,
      disabled: !this.hasSelectedTableIstatCustomer() || this.istatProfileIsChanged(),
      action: (): void => {
        this.downloadIstatFile();
      },
    };

    const generatePdfIstatAction: IActionButton = {
      buttonStyle: 'primary',
      description: this.translation.buttonGeneratePdfIstat,
      tooltip: this.translation.tooltip.download.invoice,
      disabled: !this.hasSelectedTableIstatCustomer() || this.istatProfileIsChanged(),
      action: (): void => {
        this.downloadIstatReport();
      },
    };

    /**
     * Se il tipo selezionato è stampabile → Report PDF,
     * altrimenti se è online → invio online,
     * altrimenti → generazione File.
     */
    const action = this.isSelectedIstatTypePrintable()
      ? generatePdfIstatAction
      : this.isSelectedIstatTypeSendOnline()
        ? sendOnlineIstatAction
        : generateIstatFileAction;
    actions.push(action);

    return actions;
  });

  constructor() {
    effect(() => {
      const profile = this.featureIstatStore.selectedOwnerIstatProfile();
      if (!profile?.id) {
        return;
      }
      this.featureIstatStore.updateIstatProfileIsChanged(true);
    });
  }

  public downloadIstatReport(): void {
    this.runCityTaxCheckBeforeAction().subscribe((canProceed) => {
      if (!canProceed) return;
      this.featureIstatStore.generateIstatReportFile();
    });
  }

  public openInHouseGuestReportTab(): void {
    this.navigationService.changeTab('/in-house-guests-report');
  }

  public downloadIstatFile(): void {
    this.runCityTaxCheckBeforeAction().subscribe((canProceed) => {
      if (!canProceed) return;
      const istatType = this.featureIstatStore.selectedIstatTypeFromFilter();
      const hasSelectedTableIstatCustomer = this.featureIstatStore.hasSelectedTableIstatCustomer();

      if (istatType?.type == IstatTypeEnum.Spot) {
        // Controllo se non ho dei customer selezionati
        if (!hasSelectedTableIstatCustomer) {
          this.dialogService
            .showConfirm({
              title: this.translation.dialogGenerateIstatSpotFileTitle,
              content: this.translation.dialogGenerateIstatSpotFileContent,
              confirmButtonText: this.translation.dialogGenerateIstatSpotFileConfirmButton,
              cancelButtonText: this.translation.dialogGenerateIstatSpotFileCancelButton,
            })
            .subscribe((result) => {
              if (result.actionType === ActionType.Confirm) {
                this.featureIstatStore.generateIstatNoMovementsFile();
              } else {
                this.featureIstatStore.generateIstatClosureFile();
              }
            });
          return;
        }
      }

      this.featureIstatStore.generateIstatFile();
    });
  }

  public sendIstatOnline(): void {
    this.runCityTaxCheckBeforeAction().subscribe((canProceed) => {
      if (!canProceed) return;
      // Ricarica la tabella sia in caso di successo sia di errore
      this.featureIstatStore
        .sendIstatOnline()
        .pipe(finalize(() => this.featureIstatService.research.next()))
        .subscribe();
    });
  }

  /**
   * Gestione del controllo della tassa di soggiorno prima di eseguire un'azione
   */
  private runCityTaxCheckBeforeAction(): Observable<boolean> {
    const cityTax = this.featureIstatStore.selectedIstatTypeFromFilter()?.cityTax;

    if (!cityTax) {
      return of(true); // Nessun controllo necessario
    }

    return this.featureIstatStore
      .getCityTaxCheckInformation()
      .pipe(switchMap((info) => this.featureIstatService.handleCityTaxCheckInformation(info)));
  }

  /**
   * Apre la modale "Parametri di generazione"
   */
  public openGenerationParamsModal(): void {
    this.featureIstatService.openGenerationParamsModal(this.featureIstatStore);
  }
}
